import type { MedicationPadItemType } from '@/components/PatientPageV2/components/PrescriptionPad/PrescriptionPad.types';
import { useAppStore } from '@/state-management';
import { useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';

/**
 * Hook to add a new empty medication pad item.
 * Accepts an optional `onAdd` callback (e.g. for tracking) to keep the hook focused on state updates.
 * @param onAdd - Optional callback to trigger analytics tracking when adding additional medication.
 * @returns A function that adds a new medication pad item and optionally calls the provided callback.
 */

const useAdditionalMedication = (onAdd?: () => void): (() => void) => {
  const { medicationPadItems } = useAppStore.use.prescriptionAssistant().prescriptionPad;
  const updateMedicationPadItems = useAppStore.use.updateMedicationPadItems();

  const handleAdditionalMedication = useCallback(() => {
    const medicationItem: MedicationPadItemType = { medicationPadItemId: uuidv4() };
    const updatedMedicationPadItems = [medicationItem, ...(medicationPadItems || [])];

    updateMedicationPadItems(updatedMedicationPadItems);

    onAdd?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [medicationPadItems, onAdd]);

  return handleAdditionalMedication;
};

export default useAdditionalMedication;
